import React from 'react';
import { cn } from '../../lib/utils';
import { Cloud } from 'lucide-react';

export interface ProviderIconProps extends React.SVGProps<SVGSVGElement> {
  provider?: string;
  className?: string;
}

/**
 * ProviderIcon renders authentic, high-fidelity SVG brand vectors for cloud providers
 * (AWS, Azure, GCP, Oracle, IBM, Alibaba, DigitalOcean) with crisp responsive scaling
 * and dark/light mode compatibility.
 */
export const ProviderIcon: React.FC<ProviderIconProps> = ({
  provider = '',
  className,
  ...props
}) => {
  const p = provider.toLowerCase().trim();

  // Amazon Web Services (AWS)
  if (p === 'aws') {
    return (
      <svg
        viewBox="0 0 304 182"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={cn('w-5 h-5 shrink-0', className)}
        aria-label="AWS"
        {...props}
      >
        <g>
          <path
            fill="currentColor"
            d="M86.4 66.4c0 3.7.4 6.7 1.1 8.9.8 2.2 1.8 4.6 3.2 7.2.5.8.7 1.6.7 2.3 0 1-.6 2-1.9 3l-6.3 4.2c-.9.6-1.8.9-2.6.9-1 0-2-.5-3-1.4-1.4-1.5-2.6-3.1-3.6-4.7-1-1.7-2-3.6-3.1-5.9-7.8 9.2-17.6 13.8-29.4 13.8-8.4 0-15.1-2.4-20-7.2-4.9-4.8-7.4-11.2-7.4-19.2 0-8.5 3-15.4 9.1-20.6 6.1-5.2 14.2-7.8 24.5-7.8 3.4 0 6.9.3 10.6.8 3.7.5 7.5 1.3 11.5 2.2v-7.3c0-7.6-1.6-12.9-4.7-16-3.2-3.1-8.6-4.6-16.3-4.6-3.5 0-7.1.4-10.8 1.3-3.7.9-7.3 2-10.8 3.4-1.6.7-2.8 1.1-3.5 1.3-.7.2-1.2.3-1.6.3-1.4 0-2.1-1-2.1-3.1v-4.9c0-1.6.2-2.8.7-3.5.5-.7 1.4-1.4 2.8-2.1 3.5-1.8 7.7-3.3 12.6-4.5 4.9-1.3 10.1-1.9 15.6-1.9 11.9 0 20.6 2.7 26.2 8.1 5.5 5.4 8.3 13.6 8.3 24.6v37.6zm-40.6 15.2c3.3 0 6.7-.6 10.3-1.8 3.6-1.2 6.8-3.4 9.5-6.4 1.6-1.9 2.8-4 3.4-6.4.6-2.4 1-5.3 1-8.7v-4.2c-2.9-.7-6-1.3-9.2-1.7-3.2-.4-6.3-.6-9.4-.6-6.7 0-11.6 1.3-14.9 4-3.3 2.7-4.9 6.5-4.9 11.5 0 4.7 1.2 8.2 3.7 10.6 2.4 2.4 5.9 3.6 10.5 3.7zM126.1 92.4c-1.8 0-3-.3-3.8-1-.8-.6-1.5-2-2.1-3.9L96.7 10.2c-.6-2-.9-3.3-.9-4 0-1.6.8-2.5 2.4-2.5h9.8c1.9 0 3.2.3 3.9 1 .8.6 1.4 2 2 3.9l16.8 66.2 15.6-66.2c.5-2 1.1-3.3 1.9-3.9.8-.6 2.2-1 4-1h8c1.9 0 3.2.3 4 1 .8.6 1.5 2 1.9 3.9l15.8 67 17.3-67c.6-2 1.3-3.3 2-3.9.8-.6 2.1-1 3.9-1h9.3c1.6 0 2.5.8 2.5 2.5 0 .5-.1 1-.2 1.6-.1.6-.3 1.4-.7 2.5l-24.1 77.3c-.6 2-1.3 3.3-2.1 3.9-.8.6-2.1 1-3.8 1h-8.6c-1.9 0-3.2-.3-4-1-.8-.7-1.5-2-1.9-4L156 23l-15.4 64.4c-.5 2-1.1 3.3-1.9 4-.8.7-2.2 1-4 1h-8.6zm128.5 2.7c-5.2 0-10.4-.6-15.4-1.8-5-1.2-8.9-2.5-11.5-4-1.6-.9-2.7-1.9-3.1-2.8-.4-.9-.6-1.9-.6-2.8v-5.1c0-2.1.8-3.1 2.3-3.1.6 0 1.2.1 1.8.3.6.2 1.5.6 2.5 1 3.4 1.5 7.1 2.7 11 3.5 4 .8 7.9 1.2 11.9 1.2 6.3 0 11.2-1.1 14.6-3.3 3.4-2.2 5.2-5.4 5.2-9.5 0-2.8-.9-5.1-2.7-7-1.8-1.9-5.2-3.6-10.1-5.2l-12.7-4.1c-7.3-2.3-12.7-5.7-16-10.2-3.3-4.4-5-9.3-5-14.5 0-4.2.9-7.9 2.7-11.1 1.8-3.2 4.2-6 7.2-8.2 3-2.3 6.4-4 10.4-5.2 4-1.2 8.2-1.7 12.6-1.7 2.2 0 4.5.1 6.7.4 2.3.3 4.4.7 6.5 1.1 2 .5 3.9 1 5.7 1.6 1.8.6 3.2 1.2 4.2 1.8 1.4.8 2.4 1.6 3 2.5.6.8.9 1.9.9 3.3v4.7c0 2.1-.8 3.2-2.3 3.2-.8 0-2.1-.4-3.8-1.2-5.7-2.6-12.1-3.9-19.2-3.9-5.7 0-10.2.9-13.3 2.8-3.1 1.9-4.7 4.8-4.7 8.9 0 2.8 1 5.2 3 7.1 2 1.9 5.7 3.8 11 5.5l14.2 4.5c7.2 2.3 12.4 5.5 15.5 9.6 3.1 4.1 4.6 8.8 4.6 14 0 4.3-.9 8.2-2.6 11.6-1.8 3.4-4.2 6.4-7.3 8.8-3.1 2.5-6.8 4.3-11.1 5.6-4.4 1.3-9.1 2-14.2 2z"
          />
          <path
            fill="#FF9900"
            d="M273.5 143.7c-32.9 24.3-80.7 37.2-121.8 37.2-57.6 0-109.5-21.3-148.7-56.7-3.1-2.8-.3-6.6 3.4-4.4 42.4 24.6 94.7 39.5 148.8 39.5 36.5 0 76.6-7.6 113.5-23.2 4.8-2 9.5 4.1 4.1 8.1z"
          />
          <path
            fill="#FF9900"
            d="M287.2 128.1c-4.2-5.4-27.8-2.6-38.5-1.3-3.2.4-3.7-2.4-.8-4.5 18.8-13.2 49.7-9.4 53.3-5 3.6 4.5-1 35.4-18.6 50.2-2.7 2.3-5.3 1.1-4.1-1.9 3-7.5 11.9-29.8 7.7-35.1z"
          />
        </g>
      </svg>
    );
  }

  // Microsoft Azure
  if (p === 'azure') {
    return (
      <svg
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={cn('w-5 h-5 shrink-0', className)}
        aria-label="Microsoft Azure"
        {...props}
      >
        <path
          fill="#0089D6"
          fillRule="evenodd"
          clipRule="evenodd"
          d="M15.3702 13.6799L11.3702 1.67989C11.3006 1.47291 11.1652 1.29438 10.9846 1.17159C10.804 1.0488 10.5882 0.988513 10.3702 0.999896H5.63017C5.42052 0.999354 5.21598 1.0647 5.04551 1.18672C4.87504 1.30875 4.74724 1.48127 4.68015 1.67989L0.630165 13.6799C0.577646 13.8346 0.56382 13.9998 0.589943 14.1611C0.616066 14.3225 0.681335 14.4749 0.780007 14.6052C0.878678 14.7354 1.00778 14.8395 1.15598 14.9083C1.30419 14.9771 1.46699 15.0086 1.63017 14.9999H4.56016C4.76809 14.9984 4.97035 14.932 5.13883 14.8101C5.30731 14.6883 5.43363 14.5169 5.50016 14.3199L6.11015 12.5399L9.11015 14.8099C9.28448 14.9362 9.49495 15.0028 9.71018 14.9999H14.3902C14.5517 15.0052 14.7121 14.9712 14.8576 14.901C15.0032 14.8307 15.1295 14.7263 15.2259 14.5965C15.3222 14.4668 15.3856 14.3156 15.4107 14.156C15.4359 13.9963 15.422 13.833 15.3702 13.6799ZM9.75016 14.3399C9.67748 14.3399 9.60693 14.3153 9.55015 14.2699L3.90018 10.0799L3.81016 10.0099H6.81016L6.89017 9.79988L7.89017 7.26988L10.1302 13.8999C10.1482 13.9555 10.1515 14.0148 10.1399 14.072C10.1283 14.1293 10.1022 14.1826 10.064 14.2269C10.0258 14.2711 9.97689 14.3047 9.92191 14.3245C9.86694 14.3443 9.80778 14.3496 9.75016 14.3399V14.3399ZM14.4201 14.3399H10.7002C10.7749 14.1262 10.7749 13.8935 10.7002 13.6799L6.65018 1.67989H10.3702C10.4408 1.68024 10.5095 1.70258 10.5669 1.74379C10.6242 1.78501 10.6673 1.84308 10.6902 1.9099L14.7402 13.9099C14.7538 13.9597 14.756 14.012 14.7464 14.0628C14.7369 14.1136 14.7159 14.1615 14.6851 14.203C14.6542 14.2444 14.6144 14.2783 14.5685 14.302C14.5226 14.3257 14.4718 14.3387 14.4201 14.3399V14.3399Z"
        />
      </svg>
    );
  }

  // Google Cloud Platform (GCP)
  if (p === 'gcp' || p === 'google') {
    return (
      <svg
        viewBox="0 -25 256 256"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={cn('w-5 h-5 shrink-0', className)}
        aria-label="Google Cloud Platform"
        {...props}
      >
        <g>
          <path
            d="M170.2517 56.8186l22.253-22.253 1.483-9.37C153.4367-11.6774 88.9757-7.4964 52.4207 33.9196c-10.154 11.503-17.687 25.844-21.704 40.653l7.97 -1.123 44.505-7.339 3.436-3.514c19.797-21.742 53.27-24.667 76.128-6.168l7.496.39z"
            fill="#EA4335"
          />
          <path
            d="M224.2048 73.9182c-5.115-18.836-15.616-35.769-30.217-48.722l-31.232 31.232c13.188 10.776 20.701 27.01 20.379 44.037v5.544c15.351 0 27.797 12.445 27.797 27.796 0 15.352-12.446 27.485-27.797 27.485h-55.671l-5.466 5.934v33.34l5.466 5.231h55.671c39.93.311 72.552-31.494 72.863-71.424.188-24.203-11.745-46.893-31.793-60.453"
            fill="#4285F4"
          />
          <path
            d="M71.8704 205.7957h55.593v-44.506H71.8704c-3.961 0-7.797-.851-11.399-2.497l-7.887 2.42-22.409 22.253-1.952 7.574c12.567 9.489 27.9 14.825 43.647 14.756"
            fill="#34A853"
          />
          <path
            d="M71.8704 61.4255c-39.931.238-72.107 32.802-71.869 72.732.133 22.298 10.547 43.288 28.222 56.881l32.248-32.247c-13.991-6.321-20.208-22.786-13.887-36.776 6.32-13.99 22.786-20.207 36.775-13.887 6.165 2.785 11.102 7.723 13.887 13.887l32.248-32.247c-13.721-17.937-35.041-28.424-57.624-28.343"
            fill="#FBBC05"
          />
        </g>
      </svg>
    );
  }

  // Oracle Cloud Infrastructure (OCI)
  if (p === 'oracle' || p === 'oci') {
    return (
      <svg
        viewBox="0 0 231 30"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={cn('w-5 h-5 shrink-0', className)}
        aria-label="Oracle Cloud Infrastructure"
        {...props}
      >
        <path
          d="M99.61 19.52h15.24l-8.05-13L92 30H85.27l18-28.17a4.29 4.29 0 0 1 7-.05L128.32 30h-6.73l-3.17-5.25H103l-3.36-5.23m69.93 5.23V.28h-5.72V27.16a2.76 2.76 0 0 0 .85 2 2.89 2.89 0 0 0 2.08.87h26l3.39-5.25H169.54M75 20.38A10 10 0 0 0 75 .28H50V30h5.71V5.54H74.65a4.81 4.81 0 0 1 0 9.62H58.54L75.6 30h8.29L72.43 20.38H75M14.88 30H32.15a14.86 14.86 0 0 0 0-29.71H14.88a14.86 14.86 0 1 0 0 29.71m16.88-5.23H15.26a9.62 9.62 0 0 1 0-19.23h16.5a9.62 9.62 0 1 1 0 19.23M140.25 30h17.63l3.34-5.23H140.64a9.62 9.62 0 1 1 0-19.23h16.75l3.38-5.25H140.25a14.86 14.86 0 1 0 0 29.71m69.87-5.23a9.62 9.62 0 0 1-9.26-7h24.42l3.36-5.24H200.86a9.61 9.61 0 0 1 9.26-7h16.76l3.35-5.25h-20.5a14.86 14.86 0 0 0 0 29.71h17.63l3.35-5.23h-20.6"
          fill="#C74634"
        />
      </svg>
    );
  }

  // DigitalOcean
  if (p === 'digitalocean' || p === 'do') {
    return (
      <svg
        viewBox="0 0 250 250"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={cn('w-5 h-5 shrink-0', className)}
        aria-label="DigitalOcean"
        {...props}
      >
        <path
          fill="#0080FF"
          d="M177 221.5v-34.2c36.2 0 64.3-35.9 50.4-74-5.1-14-16.4-25.3-30.5-30.4-38.1-13.8-74 14.2-74 50.4l-34.1 0c0-57.7 55.8-102.7 116.3-83.8 26.4 8.3 47.5 29.3 55.7 55.7 19.9 60.5-25.1 116.3-83.8 116.3z"
        />
        <polygon fill="#0080FF" points="177.1,187.5 143,187.5 143,153.4 177.1,153.4" />
        <polygon fill="#0080FF" points="143,213.6 116.9,213.6 116.9,187.5 143,187.5" />
        <path fill="#0080FF" d="M116.9 187.5H95v-21.9h21.9v21.9z" />
      </svg>
    );
  }

  // IBM Cloud
  if (p === 'ibm') {
    return (
      <svg
        viewBox="0 0 1000 401.15"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={cn('w-5 h-5 shrink-0', className)}
        aria-label="IBM Cloud"
        {...props}
      >
        <g fill="#1F70C1">
          <rect y="373.17" width="194.43" height="27.932" />
          <rect y="319.83" width="194.43" height="27.932" />
          <rect x="55.468" y="266.54" width="83.399" height="27.932" />
          <rect x="55.468" y="213.25" width="83.399" height="27.932" />
          <rect x="55.468" y="159.96" width="83.399" height="27.932" />
          <rect x="55.468" y="106.58" width="83.399" height="27.932" />
          <rect y="53.288" width="194.43" height="27.932" />
          <rect width="194.43" height="27.932" />
          <path d="m222.17 400.85 207.11.297c27.734 0 52.793-10.697 71.513-27.932h-278.62z" />
          <path d="m222.17 347.76h299.03c5.051-8.617 8.815-18.027 11.094-27.932h-310.12z" />
          <rect x="277.73" y="266.54" width="83.3" height="27.932" />
          <path d="m444.43 266.54v27.932h90.927c0-9.608-1.288-19.017-3.764-27.932z" />
          <path d="m497.92 213.25h-220.19v27.932h243.46c-6.34-10.698-14.165-20.107-23.277-27.932z" />
          <path d="m277.73 159.96v27.932h220.19c9.311-7.825 17.135-17.235 23.277-27.932z" />
          <rect x="277.73" y="106.58" width="83.3" height="27.932" />
          <path d="m444.43 134.51h87.163c2.476-8.914 3.764-18.324 3.764-27.932h-90.927z" />
          <path d="m521.2 53.288h-299.03v27.932h310.12c-2.575-9.905-6.339-19.314-11.093-27.932z" />
          <path d="m429.28 0h-207.11v27.932h278.53c-18.621-17.235-43.878-27.932-71.414-27.932z" />
          <polygon points="555.57 81.22 742.67 81.22 733.06 53.288 555.57 53.288" />
          <polygon points="555.57 27.932 724.25 27.932 714.64 0 555.57 0" />
          <polygon points="861.03 401.17 861.03 373.24 1000 373.24 1000 401.17" strokeWidth="1.0018" />
          <polygon points="861.03 347.76 861.03 319.83 1000 319.83 1000 347.76" />
          <polygon points="777.73 182.54 769.91 159.96 694.43 159.96 611.03 159.96 611.03 187.89 694.43 187.89 694.43 162.24 703.25 187.89 852.22 187.89 861.03 162.24 861.03 187.89 944.43 187.89 944.43 159.96 861.03 159.96 785.56 159.96" />
          <polygon points="944.43 106.58 803.98 106.58 794.37 134.51 944.43 134.51" />
          <polygon points="1000 27.932 1000 0 840.93 0 831.32 27.932" />
          <polygon points="768.13 373.22 777.73 400.85 787.34 373.22" />
          <polygon points="749.5 319.83 759.31 347.76 796.16 347.76 806.06 319.83" />
          <polygon points="730.78 266.54 740.59 294.47 814.88 294.47 824.68 266.54" />
          <polygon points="721.97 241.18 833.6 241.18 843.11 213.25 712.36 213.25" />
          <polygon points="611.03 134.51 761.09 134.51 751.49 106.58 611.03 106.58" />
          <polygon points="1000 53.288 822.4 53.288 812.9 81.22 1000 81.22" />
          <rect x="555.57" y="373.22" width="138.97" height="27.932" />
          <rect x="555.57" y="319.83" width="138.97" height="27.932" />
          <rect x="611.03" y="266.54" width="83.399" height="27.932" />
          <rect x="611.03" y="213.25" width="83.399" height="27.932" />
          <rect x="861.03" y="213.25" width="83.399" height="27.932" />
          <rect x="861.03" y="266.54" width="83.399" height="27.932" />
        </g>
      </svg>
    );
  }

  // Alibaba Cloud
  if (p === 'alibaba' || p === 'alicloud') {
    return (
      <svg
        viewBox="0 0 122 75"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={cn('w-5 h-5 shrink-0', className)}
        aria-label="Alibaba Cloud"
        {...props}
      >
        <rect x="40.1" y="32.8" fill="#ED6B1E" width="40.1" height="9" />
        <path
          fill="#ED6B1E"
          d="M100.2 0H73.7l6.4 9.1L99.5 15c3.6 1.1 5.9 4.5 5.8 8V52c0 3.6-2.3 6.9-5.8 8l-19.3 5.9L73.7 75h26.5c11.1 0 20-9 20-20V20C120.2 9 111.3 0 100.2 0"
        />
        <path
          fill="#ED6B1E"
          d="M20 0h26.5l-6.4 9.1L20.8 15c-3.6 1.1-5.9 4.5-5.8 8V52c0 3.6 2.3 6.9 5.8 8l19.3 5.9 6.4 9.1H20C9 75 0 66 0 55V20C0 9 9 0 20 0"
        />
      </svg>
    );
  }

  // Fallback icon for unknown providers
  return <Cloud className={cn('w-5 h-5 shrink-0', className)} aria-label={provider || 'Cloud'} {...props} />;
};
